import { IFormControlDefinition, IListOption, IDateRangeControl, INumberControl } from './types';
import { IBasicControlDefinition, IRadioControl, IInputControl } from './types';
import { InputType } from "reactstrap/lib/Input";
import { ValidateBoolean } from './validation/ValidateBoolean';
import { ValidateAny } from './validation/ValidateAny';
import { ValidateString } from './validation/ValidateString';
import { validateFromRoot } from './validation/helpers';
import { ValidateNumber } from './validation/ValidateNumber';
import { IOptionSetControl } from './OptionSetControl';
import { ValidateDateRange } from './validation/ValidateDateRange';
import { AttachmentType } from '../../api/types';
import { IFileControl } from './FileControl';
import { ValidateDate } from './validation/ValidateDate';
import { ICheckBoxControlDefintion } from './BasicControls';
import { Moment } from 'moment';

export const validateDefault = <T>(arg: T) => arg;

interface IFieldOptions {
    description?: string;
}
export class FieldBuilder<TRoot> {
    private key: string;
    private label: string;
    private options: IFieldOptions;

    constructor(key: string, label: string, options: IFieldOptions = {}) {
        this.key = key;
        this.label = label;
        this.options = options || {};
    }

    public checkbox(validate?: (opt: ValidateBoolean<TRoot>) => ValidateAny, customLabel?: JSX.Element): IFormControlDefinition<ICheckBoxControlDefintion> {
        return this.createDefinition('basicbox', { label: this.label, description: this.options.description, customLabel }, () => (validate || validateDefault)(new ValidateBoolean(this.label, this.key)));
    }

    public text(type: InputType = 'text', validate?: (opt: ValidateString<TRoot>) => ValidateAny): IFormControlDefinition<IInputControl> {
        return this.createDefinition('basic', { label: this.label, controlType: type, description: this.options.description }, () => (validate || validateDefault)(new ValidateString(this.label, this.key)));
    }
    public dateRange(options: {startLabel: string, endLabel: string, minDate?: Moment}, validate?: (opt: ValidateDateRange<TRoot>) => ValidateAny): IFormControlDefinition<IDateRangeControl> {
        return this.createDefinition('date-range', { label: this.label, startLabel: options.startLabel, endLabel: options.endLabel, minDate: options.minDate, controlType: 'date', description: this.options.description }, () => (validate || validateDefault)(new ValidateDateRange(this.label, this.key)));
    }
    public date(validate?: (opt: ValidateDate<TRoot>) => ValidateAny): IFormControlDefinition<IBasicControlDefinition> {
        return this.createDefinition('date', { label: this.label, description: this.options.description }, () => (validate || validateDefault)(new ValidateDate(this.label, this.key)));
    }
    public number(options: {fractionDigits?: number, currency?: boolean}, validate?: (opt: ValidateNumber<TRoot>) => ValidateAny): IFormControlDefinition<INumberControl> {
        return this.createDefinition('number', { label: this.label, controlType: 'text', description: this.options.description, ...options }, () => (validate || validateDefault)(new ValidateNumber<TRoot>(this.label, this.key).isNumber()));
    }
    public hidden(): IFormControlDefinition<any> {
        return this.createDefinition('none', {}, () => new ValidateAny(this.label, this.key));
    }

    public group(options: IListOption[], validate?: (opt: ValidateString<TRoot>) => ValidateAny): IFormControlDefinition<IRadioControl> {
        return this.createDefinition('group', { label: this.label, options }, () => (validate || validateDefault)(new ValidateString(this.label, this.key)));
    }
    public optionset(loadOptions: (context?: any) => Promise<IListOption<number | string>[]>, addDefault: boolean = true, validate?: (opt: ValidateAny<TRoot>) => ValidateAny): IFormControlDefinition<IOptionSetControl> {
        return this.createDefinition('optionset', { label: this.label, description: this.options.description, loadOptions, addDefault: !!addDefault }, () => (validate || validateDefault)(new ValidateAny(this.label, this.key)));
    }
    public multiOption(loadOptions: (context?: any) => Promise<IListOption<any>[]>, validate?: (opt: ValidateAny<TRoot>) => ValidateAny): IFormControlDefinition<IOptionSetControl> {
        return this.createDefinition('multioption', { label: this.label, description: this.options.description, loadOptions }, () => (validate || validateDefault)(new ValidateAny(this.label, this.key)));
    }
    public file(type: AttachmentType, max: number = 1, validate?: (opt: ValidateAny<TRoot>) => ValidateAny): IFormControlDefinition<IFileControl> {
        return this.createDefinition('file', { label: this.label, description: this.options.description, maxFiles: max, attachmentType: type }, () => (validate || validateDefault)(new ValidateAny(this.label, this.key)));
    }
    private createDefinition<TConfig>(type: string, config: TConfig, validator: () => ValidateAny): IFormControlDefinition<TConfig> {
        const validate = validator().build();

        return {
            config,
            key: this.key,
            type,
            required: validate.required,
            validateSelf: validate.validator,
            validateFromRoot: validate ? validateFromRoot(this.key, validate.validator) : null,
        };
    }
}
